import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import {
  languageOptions,
  globalSystemConstants,
  configurationsConstants,
  shutterbDashboardConstants,
} from "../../../../../constants";
import dummypic from "../../../../../assets/images/dummy-pic.jpg";
import {
  customToast,
  convertHeicToJpeg,
  scrollToFirstError,
} from "../../../../../shared";
import CheckBox from "../../../../../shared/components/Fields/CheckBox";
import {
  configurationsActions,
  shutterbDashboardActions,
} from "../../../../../redux/actions";
import MyPortfolioGallery from "./MyPortfolioGallery";

const allowedFormats = [
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
  ".heif",
  ".HEIC",
  ".HEIF",
];

const MyPortfolio = (props) => {
  const { ...portfolioInfo } = props?.portfolioInfo;

  const { t } = useTranslation("common");

  const [images, setImages] = useState([]);
  const [devicesData, setDevicesData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [profileImgToShow, setProfileImgToShow] = useState({});
  const [isAllFilesSelected, setIsAllFilesSelected] = useState(false);

  // Fetch devices and portfolio images on mount
  useEffect(() => {
    props.getSmartPhoneDevices(setDevicesData);
    props.getPortfolioRequests(props.sbUserId, setImages);

    //eslint-disable-next-line
  }, []);

  const handleSubmit = (e, values) => {
    if (
      !portfolioInfo?.profileImageUrl &&
      !profileImgToShow.hasOwnProperty(shutterbDashboardConstants.PREVIEW)
    ) {
      customToast(t("please_upload_profile_picture"), toast.TYPE.ERROR);
      return;
    } else if (profileImgToShow?.raw) {
      const fileName = profileImgToShow.raw.name;
      const fileExtension = fileName
        .slice(fileName.lastIndexOf("."))
        .toLowerCase();
      if (!allowedFormats.includes(fileExtension)) {
        customToast(t("profile_image_format_not_supported"), toast.TYPE.ERROR);
        return;
      }
    }
    if (images && images.length < 8) {
      customToast(t("portfolio_min_images_required"), toast.TYPE.ERROR);
      return;
    }
    let portfolioFormData = new FormData();
    images
      ? images.forEach((file) => {
          !file.hasOwnProperty("imageUrl") &&
            portfolioFormData.append("GalleryImages", file.file);
        })
      : portfolioFormData.append("GalleryImages", null);
    portfolioFormData.append("ProfileImage", profileImgToShow?.raw ?? null);
    portfolioFormData.append("sbUserId", props.sbUserId);
    portfolioFormData.append("profileName", values.profile_name);
    portfolioFormData.append("Bio", values.Bio);
    portfolioFormData.append("deviceId", values.device_type);
    portfolioFormData.append("Language", values.language_spoken);
    portfolioFormData.append(
      "additionalEquipment",
      values.additional_equipment ? values.additional_equipment : ""
    );
    portfolioFormData.append(
      "deleteImageIds",
      selectedFiles.map((f) => f.id)
    );
    portfolioFormData.append("isActive", true);
    const updatedImages = images.map((image, index) => ({
      ...image,
      displayOrder: index + 1,
    }));
    portfolioFormData.append("displayOrderList", JSON.stringify(updatedImages));

    props.updateMyPortfolio(
      props.sbUserId,
      portfolioFormData,
      setProfileImgToShow,
      setImages
    );
  };

  const handlePreview = (id) => {
    if (id) {
      window.location.href = `${process.env.REACT_APP_HOST_BASE_URL}ShutterbProfile/${id}`;
    } else {
      customToast("Profile doesnot exist", toast.TYPE.ERROR);
    }
  };

  const handleHeicFile = async (file) => {
    try {
      const result = await convertHeicToJpeg(file);

      setProfileImgToShow({
        preview: URL.createObjectURL(result),
        raw: result,
      });
    } catch (error) {
      customToast(t("image_conversion_error"), toast.TYPE.ERROR);
    } finally {
      props.diActivateSpinner();
    }
  };

  const handleSelectAll = () => {
    if (isAllFilesSelected) {
      setIsAllFilesSelected(false);
      setSelectedFiles([]);
    } else {
      setIsAllFilesSelected(true);
      setSelectedFiles(images);
    }
  };

  const handleDeleteSelected = () => {
    setIsAllFilesSelected(false);

    setImages((prevImages) =>
      prevImages.filter((image) => !selectedFiles.includes(image))
    );
  };

  const handleOpenModal = (index) => {
    setIsModalOpen(true);
    setCurrentFileIndex(index);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="shutterb-dashboard-my-portfolio mt-3">
      <div className="shutterb-form-header pt-4">
        <div className="shutterb-avatar-container">
          <img
            src={
              !profileImgToShow.hasOwnProperty(
                shutterbDashboardConstants.PREVIEW
              )
                ? portfolioInfo?.profileImageUrl
                  ? `${process.env.REACT_APP_S3_BASE_URL_PORTFOLIO}/${portfolioInfo?.profileImageUrl}`
                  : dummypic
                : profileImgToShow.preview
            }
            alt=""
            className="avatar obj-fit-cover"
          />
          <label className="camera-icon" htmlFor="profilePic">
            <svg
              width="17"
              height="14"
              viewBox="0 0 17 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.9375 2.74219H12.3008L10.7227 1.14844C10.7227 1.14844 10.7148 1.14062 10.7109 1.14062L10.7031 1.13281C10.4688 0.898438 10.1523 0.75 9.79297 0.75H6.51172C6.12891 0.75 5.78906 0.914063 5.55078 1.17578V1.17969L4.00781 2.74219H1.3125C0.585937 2.74219 0 3.3125 0 4.03906V11.9336C0 12.6602 0.585937 13.25 1.3125 13.25H14.9375C15.6602 13.25 16.25 12.6602 16.25 11.9336V4.03906C16.25 3.3125 15.6602 2.74219 14.9375 2.74219ZM8.125 11.2773C6.13672 11.2773 4.51562 9.65234 4.51562 7.66016C4.51562 5.66406 6.13672 4.04297 8.125 4.04297C10.1172 4.04297 11.7344 5.66406 11.7344 7.66016C11.7344 9.65234 10.1172 11.2773 8.125 11.2773ZM14.6914 4.83203C14.3906 4.83203 14.1445 4.58594 14.1445 4.28125C14.1445 3.97656 14.3906 3.73047 14.6914 3.73047C14.9922 3.73047 15.2383 3.97656 15.2383 4.28125C15.2383 4.58594 14.9922 4.83203 14.6914 4.83203Z"
                fill="currentColor"
              />
              <path
                d="M8.125 4.92578C6.61719 4.92578 5.39844 6.14844 5.39844 7.66016C5.39844 9.16797 6.61719 10.3945 8.125 10.3945C9.62891 10.3945 10.8516 9.17188 10.8516 7.66016C10.8516 6.14844 9.62891 4.92578 8.125 4.92578Z"
                fill="currentColor"
              />
            </svg>
          </label>
        </div>
        <p>{portfolioInfo?.profileName}</p>
      </div>
      <div className="row">
        <div className="col-12">
          <hr className="my-3" />
        </div>
      </div>

      <AvForm
        id="event-form"
        onValidSubmit={handleSubmit}
        onInvalidSubmit={(event, errors, values) => {
          scrollToFirstError(errors);
        }}
        autoComplete="off"
      >
        <div id="form-content">
          <input type="hidden" name="is_offline_process" value="no" />
          <input type="hidden" value="add_event" id="event_section" />
          {/* profile pic input */}
          <div className="profile-pic-input">
            <AvField
              className="d-none"
              type="file"
              accept=".png,.jpg,.jpeg,image/heic,image/heif,.heic,.heif,.HEIC,.HEIF"
              name="profilePic"
              onChange={(e) => {
                const file = e.target.files[0];
                const fileName = file.name;
                const fileExtension = fileName
                  .slice(fileName.lastIndexOf("."))
                  .toLowerCase();
                if (!allowedFormats.includes(fileExtension)) {
                  customToast("Format not supported", toast.TYPE.ERROR);
                  return;
                }

                if (
                  fileExtension === ".heic" ||
                  fileExtension === ".heif" ||
                  fileExtension === ".HEIC" ||
                  fileExtension === ".HEIF"
                ) {
                  props.activateSpinner();
                  handleHeicFile(file);
                } else {
                  setProfileImgToShow({
                    preview: URL.createObjectURL(file),
                    raw: file,
                  });
                }
              }}
            />
          </div>
          <div className="row">
            <div className="col-12 col-md-6">
              <AvField
                name="profile_name"
                value={portfolioInfo?.profileName}
                label={t("profile_name")}
                type="text"
                maxLength={configurationsConstants.PROFILE_NAME_LENGTH}
                validate={{
                  required: {
                    value: true,
                    errorMessage: t("this_field_is_required"),
                  },
                }}
              />
            </div>
            <div className="col-12 col-md-6 icon-field-con">
              <label htmlFor="device_type">{t("device_type")}</label>
              <AvField
                type="select"
                name="device_type"
                value={
                  portfolioInfo?.device?.id ??
                  (devicesData && devicesData.length > 0
                    ? devicesData[0].id
                    : null)
                }
              >
                {devicesData &&
                  devicesData.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.deviceName}
                    </option>
                  ))}
              </AvField>
              <div className="drop-arrow-con">
                <svg
                  width="9"
                  height="6"
                  viewBox="0 0 9 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L4.5 4.5L8 1"
                    stroke="#575756"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <AvField
                name="language"
                value="English"
                label={t("language_spoken")}
                type="text"
                readOnly
              />
            </div>
            <div className="col-12 col-md-6 icon-field-con">
              <label htmlFor="language_spoken">
                {t("additional_language")}
              </label>
              <AvField
                type="select"
                name="language_spoken"
                value={portfolioInfo?.language ?? "null"}
              >
                <option value="null" selected disabled>
                  Select Language
                </option>
                {languageOptions.map((language, index) => (
                  <option key={index}>{language}</option>
                ))}
              </AvField>
              <div className="drop-arrow-con">
                <svg
                  width="9"
                  height="6"
                  viewBox="0 0 9 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L4.5 4.5L8 1"
                    stroke="#575756"
                    stroke-width="1.16667"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="col-12">
              <label htmlFor="Bio">{t("bio")}</label>
              <AvField
                name="Bio"
                type="textarea"
                value={portfolioInfo?.bio}
                maxLength={configurationsConstants.TEXTAREA_INPUT_LENGTH}
                validate={{
                  required: {
                    value: true,
                    errorMessage: t("this_field_is_required"),
                  },
                }}
                rows={4}
              />
            </div>
            <div className="col-12">
              <label htmlFor="additional_equipment">
                {t("additional_equipment")}
              </label>
              <p className="p-0 m-0 mt-1 mb-2 additional_equipment_description">
                {t("additional_equipment_description")}
              </p>
              <AvField
                name="additional_equipment"
                type="textarea"
                value={portfolioInfo?.additionalEquipment}
                maxLength={configurationsConstants.TEXTAREA_INPUT_LENGTH}
                rows={4}
              />
            </div>
          </div>

          <div className="row py-3 mx-3">
            <div className="portfolio-gallary">
              <p>
                please select a minimum of 8, maximum of 24 (images, videos and
                recaps) for your portfolio page. hosts will get to review
                available shutterbz in their area so make sure you’re choosing
                (images, videos and recaps) that will help you stand out and
                really show your style! These (images, videos and recaps) must
                be taken from your current smartphone.
              </p>
              <div className="d-flex align-items-center pb-2">
                <CheckBox
                  labelText="select all"
                  checked={isAllFilesSelected}
                  setChecked={handleSelectAll}
                  containerClass="teal-checkbox pr-2"
                />
                <button
                  className="cursor-pointer border-0 bg-none"
                  onClick={handleDeleteSelected}
                  disabled={selectedFiles.length === 0}
                  type="button"
                >
                  <FontAwesomeIcon icon={faTrashAlt} className="clr-red" />
                </button>
              </div>
              <AvField name="Image Gallery" type="hidden" value={images} />
              <MyPortfolioGallery
                images={images}
                setImages={setImages}
                selectedFiles={selectedFiles}
                setSelectedFiles={setSelectedFiles}
                handleOpenModal={handleOpenModal}
              />
            </div>
          </div>
          <div className="row py-4">
            <div className="col-12">
              <button type="submit" className="btn-save">
                save
              </button>
              <button
                type="button"
                className="btn-preview ml-2 ml-sm-3"
                onClick={() => handlePreview(portfolioInfo?.sbuserId)}
              >
                preview
              </button>
            </div>
          </div>
        </div>
      </AvForm>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Media Preview Modal"
        className="media-preview-modal"
        overlayClassName="media-preview-modal-overlay"
      >
        <div className="modal-content">
          {images[currentFileIndex]?.file &&
            (images[currentFileIndex].file?.type.startsWith("image/") ? (
              <img
                src={
                  images[currentFileIndex]?.id
                    ? images[currentFileIndex].url
                    : URL.createObjectURL(images[currentFileIndex].file)
                }
                alt="Preview"
                className="media-preview-element"
              />
            ) : (
              <video
                src={
                  images[currentFileIndex]?.id
                    ? images[currentFileIndex].url
                    : URL.createObjectURL(images[currentFileIndex].file)
                }
                className="media-preview-element"
                controls
              />
            ))}

          <button onClick={handleCloseModal} className="modal-close-button">
            <img
              alt="Close Modal"
              className="media-preview-close-icon"
              src="/images/simpleCrossWhite.svg"
            />
          </button>
          <button
            className="arrow-button arrow-button--left"
            disabled={currentFileIndex === 0}
            onClick={() => setCurrentFileIndex((prevIndex) => prevIndex - 1)}
          >
            <img
              alt="Previous"
              className="media-preview-arrow-icon"
              src="/images/previousArrowButtonWhite.svg"
            />
          </button>
          <button
            className="arrow-button arrow-button--right"
            onClick={() => setCurrentFileIndex((prevIndex) => prevIndex + 1)}
            disabled={currentFileIndex === images.length - 1}
          >
            <img
              alt="Next"
              className="media-preview-arrow-icon"
              src="/images/nextArrowButtonWhite.svg"
            />
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  sbUserId: state.shutterbAuthentication.sbUserId,
  portfolioInfo: state.shutterbDashboard.sbMyPortfolio,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getPortfolioRequests: (id, setImages) =>
    dispatch(shutterbDashboardActions.myPortfolio(id, setImages)),
  updateMyPortfolio: (id, dataObj, setProfileImgToShow, setImages) =>
    dispatch(
      shutterbDashboardActions.updateMyPortfolio(
        id,
        dataObj,
        setProfileImgToShow,
        setImages
      )
    ),
  getSmartPhoneDevices: (setData) =>
    dispatch(configurationsActions.getSmartPhoneDevices(setData)),
  activateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_ACTIVATE, data: null }),
  diActivateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_DIACTIVATE, data: null }),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyPortfolio));
